import { useMemo, useState, type ReactNode } from 'react';
import type { ProjectEntity, TagEntity } from '../../../entities/task/model/task.types';
import type { TaskListItem } from '../model/task.types';
import {
  EMPTY_TASK_FILTERS,
  type TaskFilters,
} from '../model/task.types';
import { filterTaskListItems, hasActiveFilters } from '../model/selectors';
import { ListSkeleton, EmptyState } from '../../../shared/ui/states';
import { Button } from '../../../shared/ui/Button';
import { TaskRow } from './TaskRow';
import { TaskFiltersBar } from './TaskFiltersBar';
import { QuickAddBar, type QuickAddDefaultDate } from './QuickAddBar';
import { useIsMobileViewport } from '../../../shared/lib/useMediaQuery';
import { useTaskActions } from '../lib/useTaskActions';
import { describeRecurrenceSummary } from '../lib/parseQuickAdd';

export interface ListScreenShellProps {
  title: string;
  description: string;
  loading: boolean;
  error: string | null;
  offline: boolean;
  pendingCount: number;
  items: TaskListItem[];
  overdue?: TaskListItem[];
  /** Отдельный блок «Без даты» внизу (Сегодня). */
  undated?: TaskListItem[];
  allTags: TagEntity[];
  allProjects?: ProjectEntity[];
  /** Жёсткий project_id для QuickAdd (экран проекта). */
  forcedProjectId?: string | null;
  quickAddDate: QuickAddDefaultDate | null;
  emptyTitle: string;
  emptyDescription: string;
  onOpenEditor: (taskId: string) => void;
  onRetry?: () => void;
  onCreateTag?: (name: string, color: string) => Promise<string | null>;
  footer?: ReactNode;
}

/**
 * Оболочка экрана списка: шапка, офлайн/ошибки, фильтры, быстрое добавление,
 * блок «Просрочено», задачи, блок «Без даты», пустые состояния.
 */
export function ListScreenShell({
  title,
  description,
  loading,
  error,
  offline,
  pendingCount,
  items,
  overdue = [],
  undated = [],
  allTags,
  allProjects = [],
  forcedProjectId = null,
  quickAddDate,
  emptyTitle,
  emptyDescription,
  onOpenEditor,
  onRetry,
  onCreateTag,
  footer,
}: ListScreenShellProps) {
  const isDesktop = !useIsMobileViewport();
  const actions = useTaskActions();
  const [filters, setFilters] = useState<TaskFilters>(EMPTY_TASK_FILTERS);

  const filteredOverdue = useMemo(
    () => filterTaskListItems(overdue, filters),
    [overdue, filters],
  );
  const filteredItems = useMemo(
    () => filterTaskListItems(items, filters),
    [items, filters],
  );
  const filteredUndated = useMemo(
    () => filterTaskListItems(undated, filters),
    [undated, filters],
  );
  const filteredActive = useMemo(
    () => filteredItems.filter((item) => !item.isCompleted),
    [filteredItems],
  );
  const undatedActive = useMemo(
    () => filteredUndated.filter((item) => !item.isCompleted),
    [filteredUndated],
  );
  /** Все выполненные (с датой + без) — всегда ниже активных. */
  const allDone = useMemo(
    () => [
      ...filteredItems.filter((item) => item.isCompleted),
      ...filteredUndated.filter((item) => item.isCompleted),
    ],
    [filteredItems, filteredUndated],
  );

  const showSkeleton = loading;
  const showEmpty =
    !showSkeleton &&
    error === null &&
    filteredOverdue.length === 0 &&
    filteredActive.length === 0 &&
    allDone.length === 0 &&
    undatedActive.length === 0;

  return (
    <div className="tg-page space-y-4">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-tg-text">{title}</h1>
          <p className="mt-1 text-sm text-tg-text-secondary">{description}</p>
        </div>
      </header>

      {offline ? (
        <div className="rounded-2xl border border-tg-warning/40 bg-tg-surface px-4 py-3 text-sm text-tg-text-secondary">
          Вы офлайн. Задачи можно создавать и править — они сохранятся на устройстве
          {pendingCount > 0 ? ` и будут отправлены после подключения (${pendingCount}).` : '.'}
        </div>
      ) : null}

      {error !== null ? (
        <div className="rounded-2xl border border-tg-danger/40 bg-tg-surface px-4 py-3 text-sm">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <p className="font-medium text-tg-text">Не удалось загрузить список</p>
              <p className="mt-1 text-tg-text-secondary">{error}</p>
            </div>
            {onRetry !== undefined ? (
              <Button type="button" variant="secondary" onClick={onRetry}>
                Повторить
              </Button>
            ) : null}
          </div>
        </div>
      ) : null}

      {quickAddDate !== null && !loading ? (
        <QuickAddBar
          key={quickAddDate}
          defaultDate={quickAddDate}
          allTags={allTags}
          allProjects={allProjects}
          forcedProjectId={forcedProjectId}
          onCreateTag={onCreateTag}
        />
      ) : null}

      <TaskFiltersBar
        filters={filters}
        onChange={setFilters}
        allTags={allTags}
        allProjects={allProjects}
      />

      {showSkeleton ? <ListSkeleton rows={6} /> : null}

      {!showSkeleton && error === null ? (
        <div className="space-y-4">
          {filteredOverdue.length > 0 ? (
            <section aria-labelledby="overdue-heading">
              <h2
                id="overdue-heading"
                className="mb-2 flex items-center gap-2 text-sm font-semibold text-tg-priority-high"
              >
                <span className="inline-block h-2 w-2 rounded-full bg-tg-priority-high" />
                Просрочено
                <span className="font-normal text-tg-text-muted">({filteredOverdue.length})</span>
              </h2>
              <TaskList
                items={filteredOverdue}
                isDesktop={isDesktop}
                onOpenEditor={onOpenEditor}
                onToggleComplete={(item) => {
                  void actions.toggleTaskCompleted(item);
                }}
                onArchive={(item) => {
                  void actions.archiveTask(item.task.id);
                }}
              />
            </section>
          ) : null}

          <section aria-labelledby="list-heading">
            {filteredOverdue.length > 0 ? (
              <h2
                id="list-heading"
                className="mb-2 text-sm font-semibold text-tg-text-secondary"
              >
                Задачи
              </h2>
            ) : null}
            {filteredActive.length > 0 ? (
              <TaskList
                items={filteredActive}
                isDesktop={isDesktop}
                onOpenEditor={onOpenEditor}
                onToggleComplete={(item) => {
                  void actions.toggleTaskCompleted(item);
                }}
                onArchive={(item) => {
                  void actions.archiveTask(item.task.id);
                }}
              />
            ) : null}
          </section>

          {undatedActive.length > 0 ? (
            <section aria-labelledby="undated-heading" className="space-y-2">
              <h2
                id="undated-heading"
                className="flex items-center gap-2 text-sm font-semibold text-tg-text-secondary"
              >
                Без даты
                <span className="font-normal text-tg-text-muted">
                  ({undatedActive.length})
                </span>
              </h2>
              <TaskList
                items={undatedActive}
                isDesktop={isDesktop}
                onOpenEditor={onOpenEditor}
                onToggleComplete={(item) => {
                  void actions.toggleTaskCompleted(item);
                }}
                onArchive={(item) => {
                  void actions.archiveTask(item.task.id);
                }}
              />
            </section>
          ) : null}

          {allDone.length > 0 ? (
            <section aria-labelledby="done-heading" className="space-y-2">
              <h2
                id="done-heading"
                className="flex items-center gap-2 text-sm font-semibold text-tg-text-muted"
              >
                Выполненные
                <span className="font-normal">({allDone.length})</span>
              </h2>
              <TaskList
                items={allDone}
                isDesktop={isDesktop}
                onOpenEditor={onOpenEditor}
                onToggleComplete={(item) => {
                  void actions.toggleTaskCompleted(item);
                }}
                onArchive={(item) => {
                  void actions.archiveTask(item.task.id);
                }}
              />
            </section>
          ) : null}

          {showEmpty ? (
            <EmptyState
              title={emptyTitle}
              description={
                hasActiveFilters(filters)
                  ? 'По текущим фильтрам ничего не найдено. Попробуйте сбросить фильтры.'
                  : emptyDescription
              }
              action={
                hasActiveFilters(filters) ? (
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={() => {
                      setFilters(EMPTY_TASK_FILTERS);
                    }}
                  >
                    Сбросить фильтры
                  </Button>
                ) : null
              }
            />
          ) : null}
        </div>
      ) : null}

      {footer}
    </div>
  );
}

function TaskList({
  items,
  isDesktop,
  onOpenEditor,
  onToggleComplete,
  onArchive,
}: {
  items: TaskListItem[];
  isDesktop: boolean;
  onOpenEditor: (taskId: string) => void;
  onToggleComplete: (item: TaskListItem) => void;
  onArchive: (item: TaskListItem) => void;
}) {
  return (
    <ul className="divide-y divide-tg-border-soft/50 overflow-hidden rounded-2xl border border-tg-border-soft bg-tg-surface">
      {items.map((item) => (
        <li key={`${item.task.id}:${item.occurrenceDate ?? 'base'}`}>
          <TaskRow
            item={item}
            isDesktop={isDesktop}
            onOpen={() => {
              onOpenEditor(item.task.id);
            }}
            onToggleComplete={() => {
              onToggleComplete(item);
            }}
            onArchive={() => {
              onArchive(item);
            }}
          />
        </li>
      ))}
    </ul>
  );
}

export function recurrenceHint(task: TaskListItem): string | null {
  if (!task.isRecurring) {
    return null;
  }
  return describeRecurrenceSummary(task.task.recurrence_rule);
}
